import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Injector, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs';
import { LoaderComponent } from '@shared/components/loader/loader.component';
import { ModalListComponent } from '@shared/components/modal-list/modal-list.component';
import { ToasterModule } from '@shared/components/toaster/toaster.module';
import { APP_LOAD, AppLoad } from '@core/contracts/app-load';
import { StyleThemeService } from '@b2b/features/misc/style-theme/style-theme.service';
import { PwaInstallService } from '@asm/features/sync/shared/pwa/pwa-install.service';
import { PwaUpdateService } from '@asm/features/sync/shared/pwa/pwa-update.service';
import { LanguageService } from '@shared/tools/language.service';
import { ThemeService } from '@shared/tools/theme.service';
import { AppFeaturesService } from '@core/services/app-features.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [CommonModule, RouterModule, TranslateModule, LoaderComponent, ToasterModule, ModalListComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly styleThemeService = inject(StyleThemeService);
  private readonly appLoadServices = inject<AppLoad[]>(APP_LOAD, { optional: true }) ?? [];
  private readonly appFeaturesService = inject(AppFeaturesService);
  private readonly injector = inject(Injector);

  ngOnInit(): void {
    this.handleAppLoad();
    this.setLanguage();
    this.setTheme();
    this.loadStyle();
    this.initializeAsmPwaServices();
    this.forceResizeEvent();
  }

  handleAppLoad(): void {
    for (const service of this.appLoadServices) {
      service.load();
    }
  }

  loadStyle(): void {
    this.styleThemeService.load();
  }

  private initializeAsmPwaServices(): void {
    if (!this.appFeaturesService.isASMEnabled()) return;

    // Instantiation starts the service-worker listeners and update polling.
    this.injector.get(PwaInstallService);
    this.injector.get(PwaUpdateService);
  }

  private forceResizeEvent(): void {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);
  }

  setLanguage(): void {
    const currentLanguage = this.languageService.currentLanguage();
    this.languageService.setLanguage(currentLanguage);
  }

  setTheme(): void {
    this.themeService
      .currentTheme()
      .pipe(take(1))
      .subscribe(theme => {
        this.themeService.setTheme(theme);
      });
  }
}
